const LEADING_TIMESTAMP_PREFIX = /^\[\d{4}-\d{2}-\d{2} [A-Z][a-z]{2} \d{2}:\d{2}\](?:\n+|[ \t]+|$)/;

const CHECK_ZONE_BYTES = 32;

/** A stateful sanitizer that strips a leading timestamp prefix out of a streamed response. */
export type StreamingSanitizer = {
	/** Feed the next chunk; returns the sanitized text ready to emit, or `''` while buffering. */
	feed: (chunk: string | null | undefined) => string;
	/** Flush any buffered content once the stream ends. Idempotent — a second call returns `''`. */
	flush: () => string;
};

/**
 * Create a stateful streaming sanitizer for a single response stream.
 *
 * Buffers incoming chunks until one of:
 * - the buffer cannot possibly start with the prefix (first char is not `[`, or the second
 *   char is not a digit) — resolves immediately so short prefix-free responses (e.g. "OK")
 *   stream without waiting for the check zone to fill;
 * - the buffer reaches {@link CHECK_ZONE_BYTES};
 * - the buffer contains a newline — the prefix is always terminated before the model's first
 *   content line;
 * - `flush()` is called because the stream ended below the threshold.
 *
 * After the buffer resolves, every subsequent `feed()` call passes its chunk through unchanged.
 *
 * Usage:
 * ```ts
 * const sanitizer = createStreamingSanitizer();
 * for await (const chunk of stream) {
 *   const clean = sanitizer.feed(chunk);
 *   if (clean) emit(clean);
 * }
 * const tail = sanitizer.flush();
 * if (tail) emit(tail);
 * ```
 */
export function createStreamingSanitizer(): StreamingSanitizer {
	let buffer = '';
	let done = false;

	const cannotMatchPrefix = (): boolean => {
		if (buffer.length === 0) return false;
		if (!buffer.startsWith('[')) return true;
		return buffer.length >= 2 && !/\d/.test(buffer[1]);
	};

	const resolve = (): string => {
		const cleaned = buffer.replace(LEADING_TIMESTAMP_PREFIX, '');
		buffer = '';
		done = true;
		return cleaned;
	};

	return {
		feed: (chunk: string | null | undefined): string => {
			if (typeof chunk !== 'string' || chunk.length === 0) return '';
			if (done) return chunk;
			buffer += chunk;
			if (cannotMatchPrefix() || buffer.length >= CHECK_ZONE_BYTES || buffer.includes('\n')) {
				return resolve();
			}
			return '';
		},
		flush: (): string => {
			if (done || buffer.length === 0) {
				done = true;
				return '';
			}
			return resolve();
		},
	};
}
